import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import Sidebar from './components/Layout/Sidebar';
import Header from './components/Layout/Header';
import { AuthProvider, useAuth } from './auth';

// Lazy load pages for better performance
const Dashboard = lazy(() => import('./pages/Dashboard'));
const NewMigration = lazy(() => import('./pages/NewMigration'));
const MigrationStrategy = lazy(() => import('./pages/MigrationStrategy'));
const Extraction = lazy(() => import('./pages/Extraction'));
const MigrationDetail = lazy(() => import('./pages/MigrationDetail'));
const Configuration = lazy(() => import('./pages/Configuration'));
const Reports = lazy(() => import('./pages/Reports'));
const SettingsPage = lazy(() => import('./pages/Settings'));
const Reconciliation = lazy(() => import('./pages/Reconciliation'));
const Assistant = lazy(() => import('./pages/Assistant'));
const Login = lazy(() => import('./pages/Login'));

const PageLoader: React.FC = () => (
  <div className="flex items-center justify-center h-full min-h-64">
    <div className="flex flex-col items-center gap-3">
      <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      <p className="text-sm text-slate-500">Loading...</p>
    </div>
  </div>
);

// Protected app shell — redirects to /login when there's no token.
const ProtectedLayout: React.FC = () => {
  const { token } = useAuth();
  if (!token) return <Navigate to="/login" replace />;
  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Header />
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route element={<ProtectedLayout />}>
            <Route path="/" element={<Dashboard />} />
            <Route path="/strategy" element={<MigrationStrategy />} />
            <Route path="/migration/new" element={<NewMigration />} />
            <Route path="/migration/:id" element={<MigrationDetail />} />
            <Route path="/extraction" element={<Extraction />} />
            <Route path="/configuration" element={<Configuration />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/reconciliation" element={<Reconciliation />} />
            <Route path="/assistant" element={<Assistant />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </Suspense>
    </AuthProvider>
  );
};

export default App;
